// Minimal error reporter — wires window.onerror and unhandledrejection to
// POST a sanitized record to /api/errors, which logs via Cloudflare
// observability. Lightweight stand-in for a full APM until one is wired up.
// Each report is best-effort; failures stay silent so error handling
// itself can't cascade into more errors.

const MAX_PAYLOAD = 4000;
let lastSig = "";
let lastAt = 0;

function clip(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) : s;
}

// Strip the query string and hash before a URL leaves the browser. The
// reset-password, verify-email, and buddy-accept pages carry a live,
// single-use auth token in ?token=…; shipping the full href to our
// error sink would write that token into server logs where it stays
// valid for its ~1h lifetime. The path alone is all we need to locate
// the failing screen. See error-reporter-leaks-reset-and-verify-tokens-via-url.
function safeUrl(href: string): string {
  try {
    const u = new URL(href);
    return u.origin + u.pathname;
  } catch {
    // Not parseable — drop everything from the first ? or # to be safe.
    return href.split(/[?#]/)[0];
  }
}

async function report(payload: {
  message: string;
  stack?: string;
  url?: string;
}): Promise<void> {
  const sig = payload.message + "::" + (payload.stack ?? "").slice(0, 200);
  const now = Date.now();
  // Crude dedupe: skip identical errors within a 5s window so a render
  // loop doesn't hammer the endpoint.
  if (sig === lastSig && now - lastAt < 5000) return;
  lastSig = sig;
  lastAt = now;
  try {
    await fetch("/api/errors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      keepalive: true,
      body: JSON.stringify({
        message: clip(payload.message, 500),
        stack: payload.stack ? clip(payload.stack, MAX_PAYLOAD) : undefined,
        url: payload.url ? clip(safeUrl(payload.url), 500) : undefined,
      }),
    });
  } catch {
    /* swallow — never throw from the reporter */
  }
}

// Report a React render error caught by an ErrorBoundary. componentDidCatch
// swallows the throw so it never reaches window.onerror, so the boundary has
// to hand it here explicitly. `componentStack` is the React tree path to the
// component that threw — the single most useful field for locating a
// white-screen crash.
export function reportRenderError(
  err: unknown,
  componentStack?: string,
): void {
  const message =
    err instanceof Error ? err.message : String(err ?? "render error");
  const base = err instanceof Error && err.stack ? err.stack : "";
  const stack = componentStack ? `${base}\n--- component stack ---${componentStack}` : base;
  void report({
    message: `[render] ${message}`,
    stack: stack || undefined,
    url: window.location.href,
  });
}

export function installErrorReporter(): void {
  window.addEventListener("error", (e) => {
    void report({
      message: e.message || "window.error",
      stack: e.error?.stack,
      url: window.location.href,
    });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = (e.reason ?? {}) as { message?: unknown; stack?: unknown };
    const message =
      typeof reason.message === "string"
        ? reason.message
        : typeof e.reason === "string"
          ? e.reason
          : "unhandledrejection";
    const stack = typeof reason.stack === "string" ? reason.stack : undefined;
    void report({ message, stack, url: window.location.href });
  });
}
